import { useMemo } from "react";
import { v4 } from "uuid";

import { useUser } from "@/context/user-context";
import { useAuth } from "@/context/auth-context";
import { VariableSet } from "@/components/Playground/types";
import { Variable } from "@/components/InputVariableConfigurator/types";
import {
  useGetInputVariableSet,
  useUpdateInputVariableSet,
  useDeleteInputVariableSet,
} from "@/queries/input-variable-sets";

// Convert API format (Record<string, unknown>) to UI format (Variable[])
const convertValue = (value: unknown): string | object => {
  if (value === null) {
    return "";
  }

  if (typeof value === "object") {
    return value;
  }

  return String(value);
};

const convertToVariables = (variables: Record<string, unknown>): Variable[] =>
  Object.entries(variables).map(([key, value]) => ({
    id: v4(),
    key,
    value: convertValue(value),
  }));

// Convert UI format (Variable[]) to API format (Record<string, unknown>)
export const convertToRecord = (variables: Variable[]): Record<string, unknown> =>
  variables.reduce(
    (acc, variable) => {
      if (variable.key.trim()) {
        acc[variable.key] = variable.value;
      }
      return acc;
    },
    {} as Record<string, unknown>,
  );

export const useInputVariableSetData = (setId: string | undefined) => {
  const { activeWorkspaceId } = useUser();
  const auth = useAuth();
  const numericSetId = setId ? Number(setId) : undefined;
  const userToken = auth?.userToken || "";

  const { data, isLoading } = useGetInputVariableSet(
    activeWorkspaceId!,
    numericSetId!,
    userToken,
    !!numericSetId,
  );

  const updateMutation = useUpdateInputVariableSet(userToken);
  const deleteMutation = useDeleteInputVariableSet(userToken);

  const variableSet: VariableSet | null = useMemo(() => {
    if (!data?.input_variable_set) {
      return {
        id: "new",
        variables: [],
      };
    }

    return {
      id: data.input_variable_set.id.toString(),
      variables: convertToVariables(data.input_variable_set.variables),
      folder_id: data.input_variable_set.folder_id,
    };
  }, [data]);

  const metadata = data?.input_variable_set;

  const updateMetadata = async (updates: { name?: string }) => {
    if (!numericSetId) {
      return;
    }

    await updateMutation.mutateAsync({
      id: numericSetId,
      data: updates,
    });
  };

  const saveVariables = async (variables: Variable[]) => {
    if (!numericSetId) {
      return;
    }

    await updateMutation.mutateAsync({
      id: numericSetId,
      data: {
        variables: convertToRecord(variables),
      },
    });
  };

  const deleteVariableSet = async () => {
    if (!numericSetId) {
      return;
    }
    await deleteMutation.mutateAsync(numericSetId);
  };

  return {
    variableSet,
    metadata,
    isLoading,
    isSaving: updateMutation.isPending,
    updateMetadata,
    saveVariables,
    deleteVariableSet,
  };
};
